import Typography from '@mui/material/Typography';
import { ErrorCodes } from 'src/common/responseCodes';
import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import CommonErrorImage from 'assets/images/commonErrorBG.svg';
import NotFoundSmallImage from 'assets/images/notFoundSmall.png';
import { useQueryClient } from '@tanstack/react-query';
import { IMSI_NOT_VALID_ERROR } from 'rSimInventory/Components/RSimSessionHistory/erros/ImsiNotValid';
import { styled } from '@mui/material/styles';

const SessionHistoryRoot = styled('div', { name: 'SessionHistoryRoot' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  height: '100%',
});

const SessionHistoryImageContainer = styled('div', { name: 'SessionHistoryImageContainer' })(
  ({ theme }) => ({
    marginBottom: theme.spacing(20),
  }),
);

const SessionHistoryErrorFallback: React.FC<FallbackProps> = ({ error }) => {
  const { t } = useTranslation();

  let title = t('auth.somethingWrong');
  let message = '';
  let errorImage = <CommonErrorImage />;
  if (error.name === IMSI_NOT_VALID_ERROR) {
    title = t('sessionHistory.errors.dataUnavailable');
    message = t('sessionHistory.errors.imsiMissing');

    errorImage = <img src={NotFoundSmallImage} alt="" />;
  }

  return (
    <SessionHistoryRoot>
      <SessionHistoryImageContainer>{errorImage}</SessionHistoryImageContainer>
      <Typography variant="h1">{title}</Typography>
      {message && (
        <Typography variant="text5" color="text.secondary">
          {message}
        </Typography>
      )}
    </SessionHistoryRoot>
  );
};

export const SessionHistoryErrorBoundary: React.FC<React.PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();

  return (
    <ErrorBoundary
      onReset={() => {
        queryClient.refetchQueries({ queryKey: ['SessionHistory'] });
      }}
      FallbackComponent={SessionHistoryErrorFallback}
      onError={(error) => {
        if (error.message === ErrorCodes.AccessDenied) {
          throw error;
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
